import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Divider,
  Heading,
  Badge,
  Textarea,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { authApi } from '../services/api';

export const RegistrationDebug: React.FC = () => {
  const [email, setEmail] = useState('debug@test.com');
  const [password, setPassword] = useState('DebugPassword123!');
  const [firstName, setFirstName] = useState('Debug');
  const [lastName, setLastName] = useState('User');
  const [registrationResult, setRegistrationResult] = useState<any>(null);
  const [loginResult, setLoginResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawError, setRawError] = useState<any>(null);
  const [step, setStep] = useState<'idle' | 'registering' | 'registered' | 'logging-in' | 'logged-in'>('idle');

  const testRegistration = async () => {
    setLoading(true);
    setError(null);
    setRegistrationResult(null);
    setRawError(null);
    setStep('registering');

    const registrationData = {
      email,
      password,
      firstName,
      lastName,
    };

    console.log('Testing registration with:', registrationData);

    try {
      const response = await authApi.register(registrationData);
      console.log('Registration successful:', response);
      setRegistrationResult(response);
      setStep('registered');
    } catch (err: any) {
      console.error('Registration failed:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      
      setRawError(err);
      setStep('idle');
      
      if (err.response?.data?.error?.message) {
        setError(err.response.data.error.message);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const testLogin = async () => {
    setLoading(true);
    setError(null);
    setLoginResult(null);
    setRawError(null);
    setStep('logging-in');

    const loginData = {
      email,
      password,
    };

    console.log('Testing login with:', loginData);

    try {
      const response = await authApi.login(loginData);
      console.log('Login successful:', response);
      setLoginResult(response);
      setStep('logged-in');
      
      // Store tokens like the real app does
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      
    } catch (err: any) {
      console.error('Login failed:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      
      setRawError(err);
      setStep('registered');
      
      if (err.response?.data?.error?.message) {
        setError(err.response.data.error.message);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const testFullFlow = async () => {
    // First register
    await testRegistration();
    
    // Wait a moment for Cognito to process
    setTimeout(async () => {
      if (step === 'registered') {
        await testLogin();
      }
    }, 2000);
  };

  const clearResults = () => {
    setRegistrationResult(null);
    setLoginResult(null);
    setError(null);
    setRawError(null);
    setStep('idle');
  };

  const checkStoredAuth = () => {
    const accessToken = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');
    
    console.log('Stored auth data:', {
      accessToken: accessToken ? 'Present' : 'Missing',
      user: user ? JSON.parse(user) : 'Missing'
    });
  };

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <Heading size="lg" mb={6}>🐛 Registration & Login Debug Tool</Heading>
      
      <Tabs>
        <TabList>
          <Tab>Test Flow</Tab>
          <Tab>Registration Only</Tab>
          <Tab>Login Only</Tab>
          <Tab>Auth Status</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} />
              </FormControl>

              <FormControl>
                <FormLabel>Password</FormLabel>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </FormControl>

              <HStack spacing={2}>
                <FormControl>
                  <FormLabel>First Name</FormLabel>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </FormControl>

                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <Button 
                  colorScheme="blue" 
                  onClick={testFullFlow} 
                  isLoading={loading}
                  loadingText={step === 'registering' ? 'Registering...' : 'Logging in...'}
                >
                  Test Full Flow (Register → Login)
                </Button>

                <Button variant="outline" onClick={clearResults}>
                  Clear Results
                </Button>
              </HStack>

              <Box>
                <Text fontWeight="bold">Current Step: 
                  <Badge ml={2} colorScheme={
                    step === 'idle' ? 'gray' :
                    step === 'registering' ? 'yellow' :
                    step === 'registered' ? 'green' :
                    step === 'logging-in' ? 'yellow' :
                    'green'
                  }>
                    {step.toUpperCase()}
                  </Badge>
                </Text>
              </Box>
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Button 
                colorScheme="green" 
                onClick={testRegistration} 
                isLoading={loading && step === 'registering'}
                loadingText="Testing Registration..."
              >
                Test Registration Only
              </Button>

              {registrationResult && (
                <Alert status="success">
                  <AlertIcon />
                  <VStack align="start" spacing={2} w="100%">
                    <Text fontWeight="bold">✅ Registration Successful!</Text>
                    
                    <Box w="100%">
                      <Text fontWeight="semibold" mb={2}>Response:</Text>
                      <Textarea 
                        value={JSON.stringify(registrationResult, null, 2)} 
                        readOnly 
                        h="200px" 
                        fontSize="xs"
                        fontFamily="mono"
                      />
                    </Box>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Button 
                colorScheme="purple" 
                onClick={testLogin} 
                isLoading={loading && step === 'logging-in'}
                loadingText="Testing Login..."
              >
                Test Login Only
              </Button>

              {loginResult && (
                <Alert status="success">
                  <AlertIcon />
                  <VStack align="start" spacing={2} w="100%">
                    <Text fontWeight="bold">✅ Login Successful!</Text>
                    
                    <Box w="100%">
                      <Text fontWeight="semibold" mb={2}>Response:</Text>
                      <Textarea 
                        value={JSON.stringify(loginResult, null, 2)} 
                        readOnly 
                        h="200px" 
                        fontSize="xs"
                        fontFamily="mono"
                      />
                    </Box>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              <Button onClick={checkStoredAuth}>
                Check Stored Auth Data
              </Button>
              
              <Box>
                <Text fontWeight="bold">Current Auth Status:</Text>
                <Text>Access Token: {localStorage.getItem('accessToken') ? '✅ Present' : '❌ Missing'}</Text>
                <Text>Refresh Token: {localStorage.getItem('refreshToken') ? '✅ Present' : '❌ Missing'}</Text>
                <Text>ID Token: {localStorage.getItem('idToken') ? '✅ Present' : '❌ Missing'}</Text>
                <Text>User Data: {localStorage.getItem('user') ? '✅ Present' : '❌ Missing'}</Text>
              </Box>

              {localStorage.getItem('user') && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>Stored User:</Text>
                  <Textarea 
                    value={JSON.stringify(JSON.parse(localStorage.getItem('user') || '{}'), null, 2)} 
                    readOnly 
                    h="150px" 
                    fontSize="xs"
                    fontFamily="mono"
                  />
                </Box>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Divider my={6} />

      {error && (
        <Alert status="error">
          <AlertIcon />
          <VStack align="start" spacing={2} w="100%">
            <Text fontWeight="bold">❌ Error Occurred:</Text>
            <Text>{error}</Text>
            
            {rawError && (
              <Box w="100%">
                <Text fontWeight="semibold" mb={2}>Raw Error Details:</Text>
                <Textarea 
                  value={JSON.stringify({
                    message: rawError.message,
                    status: rawError.response?.status,
                    statusText: rawError.response?.statusText,
                    data: rawError.response?.data,
                    headers: rawError.response?.headers,
                  }, null, 2)} 
                  readOnly 
                  h="200px" 
                  fontSize="xs"
                  fontFamily="mono"
                />
              </Box>
            )}
          </VStack>
        </Alert>
      )}

      <Box mt={6}>
        <Text fontSize="sm" color="gray.600">
          <strong>API Base URL:</strong> {import.meta.env.VITE_API_BASE_URL || 'https://h7es6qyuyh.execute-api.eu-west-2.amazonaws.com/dev'}
        </Text>
        <Text fontSize="sm" color="gray.600">
          <strong>Environment:</strong> {import.meta.env.VITE_USE_MOCK_DATA === 'true' ? 'Mock Data' : 'Live API'}
        </Text>
      </Box>
    </Box>
  );
}; 